import "#legacy/utilities/ordered_ajax.js";
import "#/shared/api/api-host-global.js";
import "#/legacy/namespaces.js";
import "#legacy/utilities/iso_date.js";
import "#legacy/utilities/request_locale.js";
import "#finder-legacy/physician_finder/models/result.js";

// decimal places to include in bounds filter values. Used to eliminate jitter
// in floating point accuracy
const BOUNDS_ACCURACY = 6;

DoctorFinder.Collections.Results = class Results extends Backbone.Collection {
  static {
    this.prototype.model = DoctorFinder.Models.Result;
    this.prototype.url = window.Sparkle.apiHost
      ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.physicianSearchResults
      : window.SparkleConfig.routes.embed.physicianSearchResults;
    this.prototype.appointmentGrid = null;
    this.prototype.slotVariant = null;
    this.prototype.moreButtonVariant = null;
    this.prototype.cardElevation = null;
    this.prototype.fullSchedulePlacement = null;
    this.prototype.fullScheduleDirection = null;
    this.prototype.lat = null;
    this.prototype.lng = null;
    this.prototype.pageCount = null;
    this.prototype.perPage = 10;
    this.prototype.page = 1;
    this.prototype.resultCount = 0; // Initialize in case there's a server 500 error on fetch
    this.prototype.searchSentence = null;
    this.prototype.searchHint = null;
    this.prototype.selectedSkill = null;
    this.prototype.broadcastMessages = [];
    this.prototype.featuredBroadcastMessages = [];
    this.prototype.isFetched = false;
    this.prototype.filterDescriptions = {};
    this.prototype.errors = {};
    this.prototype.selectedView = "bio";
    this.prototype.filters = {};
    // URL params to append to each result url
    this.prototype.urlDecoration = {};
  }

  constructor() {
    super(...arguments);
    this.initialize = this.initialize.bind(this);
    this.selectPage = this.selectPage.bind(this);
    this.selectPerPage = this.selectPerPage.bind(this);
    this.selectView = this.selectView.bind(this);
    this.unselectResult = this.unselectResult.bind(this);
    this.selectResult = this.selectResult.bind(this);
    this.pauseFetching = this.pauseFetching.bind(this);
    this.resumeFetching = this.resumeFetching.bind(this);
    this.updateFilters = this.updateFilters.bind(this);
    this.setFilters = this.setFilters.bind(this);
    this.generateFetchParams = this.generateFetchParams.bind(this);
    this.parse = this.parse.bind(this);
    this.getPaginationWindow = this.getPaginationWindow.bind(this);
    this.getResultsWindow = this.getResultsWindow.bind(this);
    this.truncateAppointmentGrid = this.truncateAppointmentGrid.bind(this);
    this.decorateUrls = this.decorateUrls.bind(this);
    this.quantizeBounds = this.quantizeBounds.bind(this);
    this.prepareResults = this.prepareResults.bind(this);
    this.prepareDecoratedUrls = this.prepareDecoratedUrls.bind(this);
    this.applyPreparedResults = this.applyPreparedResults.bind(this);
    this.updatesFilters = this.updatesFilters.bind(this);
    this.areFiltersEqual = this.areFiltersEqual.bind(this);
    this.prepareAppointmentData = this.prepareAppointmentData.bind(this);
    this.truncateGridMetadata = this.truncateGridMetadata.bind(this);
    this.truncateSlotData = this.truncateSlotData.bind(this);
    this.eliminateExpiredSlots = this.eliminateExpiredSlots.bind(this);
    this.finalizeAppointmentSlots = this.finalizeAppointmentSlots.bind(this);
    this.deepdup = this.deepdup.bind(this);
  }

  initialize(options = {}) {
    this.options = options;
  }

  selectPage(page, options = {}) {
    return this.updateFilters({ page });
  }

  selectPerPage(perPage, options = {}) {
    return this.updateFilters({ per_page: perPage }, { resetPage: true });
  }

  selectView(view, options = {}) {
    const deferred = new jQuery.Deferred();

    if (!view) {
      view = "bio";
    }
    if (view === this.selectedView) {
      return deferred.resolve(false).promise();
    }

    this.selectedView = view;
    if (!options.silent) {
      this.trigger("change:selectedView", view);
    }

    return deferred.resolve(true).promise();
  }

  unselectResult() {
    return this.selectResult();
  }

  selectResult(resultGUID, options = {}) {
    const deferred = new jQuery.Deferred();

    resultGUID = this.get(resultGUID) ? resultGUID : null;
    if (resultGUID === this.selectedResultGUID) {
      return deferred.resolve(false).promise();
    }

    this.selectedResultGUID = resultGUID;
    if (!options.silent) {
      this.trigger("change:selectedResult", this.selectedResultGUID);
    }

    return deferred.resolve(true).promise();
  }

  // Skips any fetches
  pauseFetching() {
    return (this.fetchingPaused = true);
  }

  // Allow fetching again and trigger a fetch if the filters have changed since the last time we did
  resumeFetching() {
    this.fetchingPaused = false;
    // Replay only the history-replacement intent captured while paused, so a deferred route-load (or
    // required-modal gating) normalization still replaces rather than pushes once fetching resumes.
    // Nothing else is carried; resume re-applies the latest this.filters, the same as before this fix.
    const replaceBrowserHistory = this.pendingReplaceBrowserHistory || false;
    this.pendingReplaceBrowserHistory = false;
    return this.updateFilters(undefined, { replaceBrowserHistory });
  }

  updateFilters(filters, options = {}) {
    filters = _.extend({}, this.filters, filters); // We're specifically relying on Underscore's `extend` behaviour that treats `undefined` values as an instruction to remove that key, as opposed to jQuery's where `undefined` means don't modify that key.
    return this.setFilters(filters, options);
  }

  // Returns a promise the resolves/fails with a boolean argument that is true if the filters changed, and false if they did not.
  setFilters(filters, options = {}) {
    const deferred = new jQuery.Deferred();

    filters = {
      ...filters,
    }; // Ensure filters passed in are not modified
    if (options.resetPage) {
      filters.page = 1;
    }

    this.filters = Sparkle.Utilities.sanitizeParams(filters); // Sanitize so users of this value get a canonical form
    const newFetchParams = this.generateFetchParams();

    // If we're not making changes, bail out here
    const areFiltersEqual = this.areFiltersEqual(this.lastFetchParams, newFetchParams);
    if (this.fetchingPaused) {
      // While paused, change:filters won't fire until resumeFetching. Preserve only the route-load
      // history-replacement intent across the init/gating sequence (sticky); other options (e.g.
      // resetPage) have already been applied to this.filters and must not bleed into resume. Only a
      // real change is worth replaying, not a no-op.
      if (options.replaceBrowserHistory && !areFiltersEqual) {
        this.pendingReplaceBrowserHistory = true;
      }
      return deferred.resolve(!areFiltersEqual).promise();
    }
    if (areFiltersEqual) {
      return deferred.resolve(false).promise();
    }

    this.lastFetchParams = newFetchParams; // Store the last fetch params so we can compare the next time the filters are set
    const requestGeneration = (this.requestGeneration ?? 0) + 1;
    this.requestGeneration = requestGeneration;

    // A function to use as a beforeSend callback so we have access to the url that will be fetched
    let fullRequestUrl = null;
    const beforeSendCallback = (jqXHR, xhrSettings) => {
      fullRequestUrl = xhrSettings.url;
      this.trigger("request", fullRequestUrl, newFetchParams, requestGeneration);
      if (!options.silent) {
        return this.trigger("change:filters", this.filters, newFetchParams, fullRequestUrl, options);
      }
    };

    Sparkle.Utilities.OrderedAJAX.ajax("doctor-finder-results", {
      url: this.url,
      data: newFetchParams,
      dataType: "json",
      beforeSend: beforeSendCallback,
    })
      .progress((inOrder) => {
        // Trigger sync if the result is out-of-order and therefore will not be processed so we don't have an imbalance
        // of requests to syncs which would break the loading indicator
        if (!inOrder) {
          return this.trigger("sync");
        }
      })
      .then((response) => {
        this.reset(this.parse(response), { silent: true }); // We don't want to trigger any callbacks because we're simulating a Backbone `sync` event, so this reset is just an implementation detail of that process
        deferred.resolve(true, newFetchParams, fullRequestUrl);
        if (!options.silent) {
          return this.trigger("change:results", this, newFetchParams, fullRequestUrl, {
            generation: requestGeneration,
          });
        }
      })
      .fail(() => {
        this.trigger("request:error", newFetchParams, fullRequestUrl, { generation: requestGeneration });
        return deferred.fail(false);
      })
      .always(() => {
        // Change the isFetched flag to true so UI knows we have finished getting values for the first time
        this.isFetched = true;
        // Trigger sync after setting results or we will respond to an old list
        return this.trigger("sync");
      });

    return deferred.promise();
  }

  generateFetchParams({ onlyFilters } = {}) {
    let params = _.defaults({}, this.filters, {
      slots_ui_start_date: Sparkle.Utilities.isoDate(moment()), // Ensure cache keys include a grid start date when we're defaulting to "today"
      page: 1, // Ensure there is always a page param to avoid cache miss when paginating back to the first page
      per_page: this.options.defaultPerPage, // Ensure there is always a per_page param to avoid cache miss until the value is changed
      sort: "best_match", // Ensure there is always a sort param to avoid cache misses
      slots_ui: this.options.slotsUI,
    }); // Let the server know if we want a specific UI for slots

    // Inject additional options as search params
    params = _.extend(params, this.options.scope, {
      open_scheduling: this.options.openScheduling,
      passive_boosts: this.options.passiveBoosts,
    });

    // Snap the bounds to avoid jitter in floating point causing unwanted requests
    if (params.bounds) {
      params.bounds = this.quantizeBounds(params.bounds, BOUNDS_ACCURACY);
    }

    // Forward the active locale so server-rendered strings in the payload (scheduling
    // labels, result titles) match the page language.
    const locale = Sparkle.Utilities.requestLocale();
    if (locale) {
      params.locale = locale;
    }

    // We delete the params instead of not adding them in case they already exist before we inject the defaults.
    if (onlyFilters) {
      delete params.slots_ui_start_date;
      delete params.page;
      delete params.per_page;
      delete params.sort;
    }

    // Ensure params are in a canonical form so HTTP caching key cardinality is minimized
    params = Sparkle.Utilities.sanitizeParams(params);

    return params;
  }

  parse(response) {
    this.originalResponse = response;
    this.prepareResults();
    this.showOpenScheduling = response.show_open_scheduling;
    this.lat = response.lat;
    this.lng = response.lng;
    this.pageCount = response.page_count;
    this.perPage = response.per_page;
    this.page = response.page;
    this.filterDescriptions = response.filter_descriptions;
    this.resultCount = response.result_count;
    this.physiciansResultCount = response.physicians_result_count;
    this.locationsResultCount = response.locations_result_count;
    this.searchSentence = response.search_sentence;
    this.searchHint = response.search_hint;
    this.selectedSkill = response.selected_skill;
    this.broadcastMessages = response.broadcast_messages || [];
    this.noResultsMessage = response.no_results_message;
    this.featuredBroadcastMessages = response.featured_broadcast_messages;
    this.availableAgeGroups = response.available_age_groups;
    this.errors = response.errors;
    this.currentPageBegin = Math.min(1 + (this.page - 1) * this.perPage, this.resultCount);
    this.currentPageEnd = Math.min(this.page * this.perPage, this.resultCount);
    this.isEmpty = this.resultCount === 0; // Only set once a fetch has been performed
    this.isCardsSlotsUI = response.slots_ui === "cards";
    this.slotVariant = response.slot_button_variant;
    this.moreButtonVariant = response.more_button_variant;
    this.cardElevation = response.card_elevation;
    this.fullSchedulePlacement = response.full_schedule_placement;
    this.fullScheduleDirection = response.full_schedule_direction;
    // An action-cached response can predate the key, and EJS renders a missing value as the
    // string "undefined".
    this.slotShowYear = response.slot_show_year || "never";

    return this.preparedResults;
  }

  // Outputs a list of pages for rendering a pagination control. The window's width is how many discrete pages to show in the list.
  getPaginationWindow(width = 5) {
    const pages = [];
    let page = Math.max(this.page - Math.floor(width / 2), 1);
    while (page <= this.pageCount && pages.length < width) {
      pages.push({ number: page, current: page === this.page });
      page++;
    }

    return pages;
  }

  getResultsWindow() {
    return {
      start: (this.page - 1) * this.perPage + 1,
      end: Math.min(this.page * this.perPage, this.resultCount),
    };
  }

  truncateAppointmentGrid(appointmentGridWidth, truncate = true) {
    this.appointmentGridWidth = appointmentGridWidth;
    this.truncate = truncate;
    this.prepareResults();
    return this.applyPreparedResults();
  }

  decorateUrls(urlParams) {
    jQuery.extend(this.urlDecoration, urlParams);
    this.prepareResults();
    return this.applyPreparedResults();
  }

  // PRIVATE

  quantizeBounds(bounds, accuracy) {
    bounds = {
      ...bounds,
    };
    bounds.north = bounds.north.toFixed(accuracy);
    bounds.south = bounds.south.toFixed(accuracy);
    bounds.east = bounds.east.toFixed(accuracy);
    bounds.west = bounds.west.toFixed(accuracy);

    return bounds;
  }

  prepareResults() {
    if (!this.originalResponse) {
      return;
    } // Bail out if we call this method before fetching data from the server

    this.preparedResults = this.deepdup(this.originalResponse.results);
    this.prepareAppointmentData();
    return this.prepareDecoratedUrls();
  }

  prepareDecoratedUrls() {
    if (_.isEmpty(this.urlDecoration)) {
      return;
    }

    return this.preparedResults.forEach((result) => {
      if (!result.url) {
        return;
      }
      const newUrl = new URL(result.url);
      _.each(this.urlDecoration, (value, key) =>
        newUrl.searchParams.set(key, _.isFunction(value) ? value.call(result, result) : value),
      );
      return (result.url = newUrl.href);
    });
  }

  applyPreparedResults() {
    // Update the collection models with the prepared JSON
    this.reset([], { silent: true }); // Ensure that any attribute keys that have been deleted will be removed from each result
    return this.set(this.preparedResults);
  }

  // Returns true if the the given filters would cause changes to the current filter set
  updatesFilters(filters) {
    return !this.areFiltersEqual(_.pick(this.filters, ...Array.from(_.keys(filters))), filters);
  }

  areFiltersEqual(a, b) {
    return jQuery.param(Sparkle.Utilities.sanitizeParams(a)) === jQuery.param(Sparkle.Utilities.sanitizeParams(b)); // Convert the filters to params to avoid type-mismatches causing inequality. e.g. "true" != true
  }

  prepareAppointmentData() {
    this.eliminateExpiredSlots(this.preparedResults);

    // Reset the existing appointment grid so it is not stale and used in the UI
    this.appointmentGrid = null;

    if (!this.originalResponse.appointment_grid) {
      return;
    } // Bail out if we don't have any appointment grid data, e.g. scheduling is disabled

    this.appointmentGrid = this.deepdup(this.originalResponse.appointment_grid);

    if (this.truncate) {
      this.truncateGridMetadata(this.appointmentGrid);
      this.truncateSlotData(this.preparedResults);
    }

    return this.trigger("change:appointmentGrid", this.appointmentGrid);
  }

  truncateGridMetadata(appointmentGrid) {
    if (appointmentGrid.previous) {
      appointmentGrid.previous = Sparkle.Utilities.isoDate(
        moment(appointmentGrid.dates[0]).subtract(this.appointmentGridWidth, "days"),
      );
    }
    appointmentGrid.next = appointmentGrid.dates[this.appointmentGridWidth];
    appointmentGrid.dates.splice(this.appointmentGridWidth);

    return appointmentGrid;
  }

  truncateSlotData(results) {
    return results.forEach((result) => {
      if (result.appointment_slots != null) {
        result.appointment_slots.splice(this.appointmentGridWidth);
      }
      return this.finalizeAppointmentSlots(result);
    });
  }

  eliminateExpiredSlots(results) {
    return results.forEach((result) => {
      if (result.appointment_slots != null) {
        result.appointment_slots.forEach(
          (appointmentSlot, index) =>
            (appointmentSlot.slots = appointmentSlot.slots.filter((slot) => new Date(slot.start_time) > new Date())),
        );
      }
      return this.finalizeAppointmentSlots(result);
    });
  }

  finalizeAppointmentSlots(result) {
    if (result.appointment_slots) {
      // Delete the appointmentSlots key if there are no slots in the grid
      let hasSlots = false;
      result.appointment_slots.forEach((appointmentSlot) => (hasSlots = hasSlots || !!appointmentSlot.slots.length));
      if (!hasSlots) {
        return delete result.appointment_slots;
      }
    }
  }

  deepdup(object) {
    return JSON.parse(JSON.stringify(object));
  }
};
